import { useToast } from 'lib/hooks';
import { ReactNode, useEffect, useState } from 'react';
import { useAuthContext } from '../context';
import { PermissionName } from '../types';
import * as api from './api';
import { ImpersonationContext } from './context';
import { getRedirectPath } from './helpers';
import { ImpersonatedUserView } from './ImpersonatedUserView';
import { ImpersonationEventModal } from './ImpersonationEventModal';
import { ImpersonationToolbar } from './ImpersonationToolbar';
import { StartImpersonationView } from './StartImpersonationView';
import { IImpersonatedUser, ImpersonationEvent } from './types';

interface IImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider = ({
  children,
}: IImpersonationProviderProps) => {
  const { getIsInPermission } = useAuthContext();
  const { message } = useToast();

  const [impersonatedUser, setImpersonatedUser] =
    useState<IImpersonatedUser | null>(null);
  const [impersonationEvent, setImpersonationEvent] =
    useState<ImpersonationEvent | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isToolbarVisible, setIsToolbarVisible] = useState<boolean>(false);

  const getImpersonatedUser = async (): Promise<IImpersonatedUser | null> => {
    setIsLoading(true);
    const res = await api.getImpersonatedUser();
    setIsLoading(false);
    return res.error ? null : res.data;
  };

  const startImpersonation = async (userId: number) => {
    setIsLoading(true);
    await api.startImpersonation(userId);

    const res = await api.getImpersonatedUser();
    if (res.error) {
      message.error(
        'Impersonation failed: User either does not exist or you have insufficient permissions.'
      );
      setIsLoading(false);
      return;
    }

    window.location.href = getRedirectPath(res.data);
  };

  const endImpersonation = async () => {
    setIsLoading(true);
    await api.endImpersonation();
    window.location.href = '/';
  };

  useEffect(() => {
    const initialize = async () => {
      const impersonatedUser = await getImpersonatedUser();
      setImpersonatedUser(impersonatedUser);
      setIsToolbarVisible(!!impersonatedUser);
    };

    initialize();
  }, []);

  useEffect(() => {
    const handleWindowFocus = async () => {
      if (isLoading) {
        return;
      }

      const nextImpersonatedUser = await getImpersonatedUser();
      if (
        nextImpersonatedUser &&
        impersonatedUser?.id !== nextImpersonatedUser.id
      ) {
        setImpersonationEvent(ImpersonationEvent.STARTED);
        return;
      }

      if (impersonatedUser && !nextImpersonatedUser) {
        setImpersonationEvent(ImpersonationEvent.ENDED);
        return;
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [impersonatedUser, isLoading]);

  useEffect(() => {
    if (!getIsInPermission(PermissionName.INTERNAL_PII)) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && !impersonatedUser) {
        setIsToolbarVisible(!isToolbarVisible);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [impersonatedUser, isToolbarVisible]);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedUser,
        startImpersonation,
        endImpersonation,
      }}
    >
      <ImpersonationToolbar isVisible={isToolbarVisible}>
        {impersonatedUser ? (
          <ImpersonatedUserView />
        ) : (
          <StartImpersonationView />
        )}
      </ImpersonationToolbar>

      {children}

      <ImpersonationEventModal event={impersonationEvent} />
    </ImpersonationContext.Provider>
  );
};
